import React from 'react';
import { Card } from '../common/Card';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';
import { homeElevatedInset, homeDarkHeroShell, homeDarkStatTile } from './homeDashboardStyles';

const homeHorizontalStripSkeletonClass = `flex gap-4 overflow-x-auto pb-2 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none touch-pan-x`;
const homeHorizontalCardSkeletonClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

function Block({ className = '', dark = false }) {
  const base = dark
    ? 'animate-pulse rounded-xl bg-white/[0.1] border border-white/[0.07]'
    : 'animate-pulse rounded-2xl bg-surface-subtle/90 border border-border/50';
  return <div className={`${base} ${className}`} aria-hidden />;
}

/** Skeleton for the dark welcome hero + last session snapshot. */
export function HomeLastSessionHeroSkeleton() {
  return (
    <div className={`${homeDarkHeroShell} mb-2 sm:mb-4`} aria-hidden>
      <div className="p-5 sm:p-7">
        <Block dark className="h-8 w-52 mb-5 rounded-xl" />
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`${homeDarkStatTile} min-h-[4.75rem] sm:min-h-[5.25rem] ${
                i === 0 ? 'hidden lg:flex lg:flex-col' : i === 4 ? 'hidden md:flex md:flex-col' : 'flex flex-col'
              }`}
            >
              <Block dark className="h-2 w-full rounded-md" />
              <Block dark className="h-7 w-2/3 rounded-lg mt-3" />
            </div>
          ))}
        </div>
        <Block dark className="mt-3 sm:mt-4 h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}

function QuickActionTileSkeleton() {
  return (
    <div
      className="animate-pulse rounded-2xl border border-border-light bg-white shadow-brand-md ring-1 ring-black/[0.04] dark:border-border-medium dark:bg-surface dark:ring-white/[0.04] h-[6rem] sm:h-[6.85rem] md:min-h-[6.85rem]"
      aria-hidden
    />
  );
}

/** Quick actions skeleton — headings on page bg, tiles match quick-action buttons */
export function HomeQuickActionsSkeleton() {
  return (
    <div className="mt-8">
      <Block className="h-7 w-40 mb-5" />
      <div className={`${interactiveBleedInsetX} pb-2 pt-1 sm:pb-2.5 sm:pt-1.5`}>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <QuickActionTileSkeleton key={i} />
        ))}
        </div>
      </div>
    </div>
  );
}

export function HomeNextGameSkeleton() {
  return (
    <div className="mt-8 mb-10" aria-hidden>
      <div className="mb-4 h-7 w-44 animate-pulse rounded-2xl border border-border/50 bg-surface-subtle/90" />
      <Card variant="default" className={`${homeElevatedInset} flex items-center gap-3 px-4 py-3`}>
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-surface-subtle" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-surface-subtle" />
          <div className="h-3 w-4/5 animate-pulse rounded-md bg-surface-subtle" />
        </div>
        <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-surface-subtle" />
      </Card>
    </div>
  );
}

export function HomeSessionsCarouselSkeleton() {
  return (
    <div aria-hidden>
      <div className="mb-5 flex items-center justify-between gap-3">
        <Block className="h-7 w-36" />
        <Block className="h-5 w-16" />
      </div>
      <div className={homeHorizontalStripSkeletonClass}>
        {[0, 1, 2].map((k) => (
          <div key={k} className={`${homeHorizontalCardSkeletonClass} flex flex-col`}>
            <Block className="mb-2 h-3 w-28" />
            <Card
              variant="default"
              className={`${homeElevatedInset} flex flex-1 flex-row items-center gap-3`}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <Block className="h-5 w-2/3 rounded-md" />
                  <Block className="h-3 w-16 rounded-md" />
                </div>
                <Block className="h-3 w-1/2 rounded-md" />
                <Block className="h-1.5 w-full rounded-full mt-auto" />
              </div>
              <Block className="h-5 w-5 shrink-0 rounded" />
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HomeHappeningSoonSkeleton() {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Block className="h-7 w-44" />
        <Block className="h-8 w-20" />
      </div>
      <div className="flex gap-4 overflow-hidden pb-2">
        {[0, 1, 2].map((k) => (
          <Card key={k} variant="default" className="shrink-0 w-[min(100%,18.5rem)] sm:w-80 p-4 border border-border-medium rounded-2xl shadow-brand-sm">
            <Block className="h-5 w-[92%] mb-3" />
            <Block className="h-3 w-full mb-2" />
            <Block className="h-3 w-2/3 mb-4" />
            <Block className="h-10 w-full" />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function HomeYourClubsSkeleton() {
  return (
    <div className="mb-10">
      <Block className="h-7 w-36 mb-5" />
      <div className="flex flex-col gap-4 sm:flex-row pb-2 sm:gap-4 sm:overflow-hidden">
        {[0, 1, 2].map((k) => (
          <Card
            key={k}
            variant="default"
            className={`${k > 0 ? 'max-sm:hidden' : ''} w-full shrink-0 sm:w-72 p-0 overflow-hidden border border-border-medium rounded-2xl shadow-brand-sm flex flex-col`}
          >
            <Block className="h-[5.25rem] sm:h-32 w-full rounded-none flex-shrink-0" />
            <div className="p-5 flex flex-col flex-1 gap-3">
              <div className="flex justify-between gap-2">
                <Block className="h-5 w-3/5 rounded-lg flex-1 min-w-0" />
                <Block className="h-7 w-16 rounded-full flex-shrink-0" />
              </div>
              <Block className="h-11 w-full rounded-xl" />
            {/* Ranking teaser skeleton */}
            <div className="pt-3 border-t border-border/40 space-y-2">
              <Block className="h-3 w-1/3 rounded-md" />
              <div className="flex items-center justify-between">
                <Block className="h-3 w-1/4 rounded-md" />
                <Block className="h-4 w-1/3 rounded-md" />
              </div>
              <div className="flex items-center justify-between">
                <Block className="h-3 w-1/3 rounded-md" />
                <Block className="h-4 w-1/4 rounded-md" />
              </div>
              <Block className="h-6 w-full rounded-lg mt-1" />
            </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function HomeActivitySkeleton() {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Block className="h-7 w-44 max-w-[min(100%,12rem)]" />
        <Block className="h-8 w-20" />
      </div>
      <div className={homeHorizontalStripSkeletonClass}>
        {[0, 1, 2].map((k) => (
          <Card
            key={k}
            variant="default"
            className={`${homeHorizontalCardSkeletonClass} p-5 flex flex-col border border-border-medium rounded-2xl shadow-brand-sm`}
          >
            <Block className="h-12 w-full mb-4 rounded-xl" />
            <Block className="h-2 w-full mb-3 rounded-full" />
            <Block className="h-4 w-1/2 mt-auto rounded-md" />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function HomeMatchupsSkeleton() {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Block className="h-7 w-40 max-w-[min(100%,11rem)]" />
        <Block className="h-8 w-20" />
      </div>
      <div className={homeHorizontalStripSkeletonClass}>
        {[0, 1, 2].map((k) => (
          <Card
            key={k}
            variant="default"
            className={`${homeHorizontalCardSkeletonClass} p-5 flex flex-col gap-3 border border-border-medium rounded-2xl shadow-brand-sm`}
          >
            <div className="flex items-center gap-2.5">
              <Block className="h-9 w-9 rounded-xl shrink-0" />
              <Block className="h-3 w-28 rounded-md" />
            </div>
            <Block className="h-4 w-3/5 rounded-lg" />
            <Block className="h-3 w-full rounded-md" />
            <Block className="h-9 w-full rounded-xl mt-auto" />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function HomeMomentsSkeleton() {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Block className="h-7 w-36 max-w-[min(100%,10rem)]" />
        <Block className="h-8 w-24" />
      </div>
      <div className={homeHorizontalStripSkeletonClass}>
        {[0, 1, 2].map((k) => (
          <Card
            key={k}
            variant="default"
            className={`${homeHorizontalCardSkeletonClass} p-5 flex flex-col gap-3 border border-border-medium rounded-2xl shadow-brand-sm`}
          >
            <div className="flex items-center gap-2.5">
              <Block className="h-9 w-9 rounded-xl shrink-0" />
              <Block className="h-3 w-24 rounded-md" />
            </div>
            <Block className="h-10 w-full rounded-xl" />
            <Block className="h-3 w-24 rounded-md mt-auto" />
          </Card>
        ))}
      </div>
    </div>
  );
}
